import { gql, type TypedDocumentNode } from '@apollo/client'
import { ProfileBasicInfo, ProfileMediaInfo } from '@web/src/lib/fragments'
import type {
  AdminReportCaseQuery,
  AdminReportCaseQueryVariables,
  AdminReportCasesQuery,
  AdminReportCasesQueryVariables,
  AdminReportMessageContextQuery,
  AdminReportMessageContextQueryVariables,
} from 'types/graphql'

export const ADMIN_REPORT_CASES_QUERY: TypedDocumentNode<AdminReportCasesQuery, AdminReportCasesQueryVariables> = gql`
  query AdminReportCasesQuery($after: String, $first: Int, $filter: AdminReportCaseFilter) {
    adminReportCasesPage(after: $after, first: $first, filter: $filter) {
      entries {
        id
        publicReference
        createdAt
        legalTrack
        priorityGroup
        priorityOverride
        status
        slaDueAt
        copyrightRestoreEligibleAt
        copyrightRestoreDeadlineAt
        liveTargetState
        allegationCount
        oldestAllegationAt
        depictedPersonRequest
        actionFailure
        target {
          kind
          id
          path
          liveState
        }
        subject {
          profileId
          userId
          displayName
        }
      }
      cursor
      hasNextPage
      count
    }
  }
`

export const ADMIN_REPORT_CASE_QUERY: TypedDocumentNode<AdminReportCaseQuery, AdminReportCaseQueryVariables> = gql`
  ${ProfileBasicInfo}
  ${ProfileMediaInfo}

  query AdminReportCaseQuery($id: String!) {
    adminReportCase(id: $id) {
      id
      publicReference
      createdAt
      legalTrack
      priorityGroup
      priorityOverride
      status
      slaDueAt
      preservationHold
      liveTargetState
      outcome
      outcomeReason
      resolvedAt
      resolvedByProfileId
      actionFailure
      depictedPersonRequest
      copyrightNoticeReceivedAt
      copyrightRestoreEligibleAt
      copyrightRestoreDeadlineAt
      copyrightStrikeRecorded
      allegationCount
      oldestAllegationAt
      target {
        kind
        id
        path
        liveState
        snapshot
      }
      allegations {
        id
        createdAt
        reason
        details
        reporterIsDepicted
        immediateDanger
        anonymousToStaff
        status
        reporter {
          anonymous
          profileId
          displayName
          profile {
            ...ProfileBasicInfo
          }
        }
      }
      subject {
        profileId
        userId
        displayName
        moderationWarningCount
        moderationLastWarnedAt
        banned
        bannedUntil
        banReason
        relatedOpenCaseCount
        relatedResolvedCaseCount
        priorOutcomes
        profile {
          ...ProfileBasicInfo
        }
      }
      blockSummary {
        activeBlockedByCount
        activeBlockingCount
      }
      filings {
        id
        createdAt
        authority
        reference
        filedAt
        filedByProfileId
        filedByDisplayName
        notes
      }
      notes {
        id
        createdAt
        authorProfileId
        author {
          ...ProfileBasicInfo
        }
        body
      }
      actions {
        id
        createdAt
        actorProfileId
        kind
        outcome
        reason
        actor {
          ...ProfileBasicInfo
        }
      }
    }
  }
`

export const ADMIN_REPORT_MESSAGE_CONTEXT_QUERY: TypedDocumentNode<
  AdminReportMessageContextQuery,
  AdminReportMessageContextQueryVariables
> = gql`
  query AdminReportMessageContextQuery($caseId: String!, $before: Int, $after: Int) {
    adminReportMessageContext(caseId: $caseId, before: $before, after: $after) {
      conversationId
      messages {
        id
        createdAt
        senderDisplayName
        content
        markedDeleted
        isReported
        mediaIds
      }
    }
  }
`
